import React from 'react';
import type { Ticket, Tech, TicketType } from '../../types/ticket';
import type { SortField, SortDirection } from './AllTicketsView';
import { formatTimeAgo } from '../../utils/dateUtils';

// Type badge colors
const TYPE_BADGE_COLORS: Record<TicketType, { bg: string; text: string; border: string }> = {
  Incident: { bg: 'rgba(209, 52, 56, 0.1)', text: '#d13438', border: 'rgba(209, 52, 56, 0.3)' },
  Request: { bg: 'rgba(0, 120, 212, 0.1)', text: '#0078d4', border: 'rgba(0, 120, 212, 0.3)' },
  Problem: { bg: 'rgba(135, 100, 184, 0.1)', text: '#8764b8', border: 'rgba(135, 100, 184, 0.3)' },
  Change: { bg: 'rgba(16, 124, 16, 0.1)', text: '#107c10', border: 'rgba(16, 124, 16, 0.3)' },
};

interface TicketTableProps {
  tickets: Ticket[];
  techs: Tech[];
  selectedTicketIds: Set<string>;
  expandedTicketId: string | null;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onSelectTicket: (ticketId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onExpandTicket: (ticketId: string | null) => void;
  onViewDetails: (ticket: Ticket) => void;
  onReassign: (ticketId: string) => void;
  onHold: (ticketId: string, reason?: string) => void;
  onChangePriority: (ticketId: string, newPriority?: string) => void;
  getSlaStatus: (ticket: Ticket) => 'ok' | 'at-risk' | 'breached';
}

const formatSlaTime = (deadline: Date | null, status: 'ok' | 'at-risk' | 'breached'): string => {
  if (!deadline) return '-';

  const now = new Date();
  const diff = new Date(deadline).getTime() - now.getTime();
  const absDiff = Math.abs(diff);
  const minutes = Math.floor(absDiff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (status === 'breached') {
    if (hours > 0) return `-${hours}h ${mins}m`;
    return `-${mins}m`;
  }

  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const SortIcon: React.FC<{ field: SortField; currentField: SortField; direction: SortDirection }> = ({
  field,
  currentField,
  direction,
}) => {
  if (field !== currentField) {
    return (
      <svg className="ticket-table__sort-icon" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4">
        <path d="M4 6l4-4 4 4M4 10l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg className="ticket-table__sort-icon ticket-table__sort-icon--active" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      {direction === 'asc' ? (
        <path d="M4 10l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
};

export const TicketTable: React.FC<TicketTableProps> = ({
  tickets,
  techs,
  selectedTicketIds,
  expandedTicketId,
  sortField,
  sortDirection,
  onSort,
  onSelectTicket,
  onSelectAll,
  onExpandTicket,
  onViewDetails,
  onReassign,
  onHold,
  onChangePriority,
  getSlaStatus,
}) => {
  const allSelected = tickets.length > 0 && tickets.every(t => selectedTicketIds.has(t.id));
  const someSelected = tickets.some(t => selectedTicketIds.has(t.id)) && !allSelected;

  const getTech = (techId: string | null): Tech | undefined => {
    if (!techId) return undefined;
    return techs.find(t => t.id === techId);
  };

  if (tickets.length === 0) {
    return (
      <div className="ticket-table__empty">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="6" y="10" width="36" height="28" rx="2" />
          <path d="M6 18h36M18 10v8M30 10v8" />
          <path d="M16 28h16M16 34h8" strokeLinecap="round" />
        </svg>
        <p className="ticket-table__empty-title">No tickets found</p>
        <p className="ticket-table__empty-subtitle">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="ticket-table__wrapper">
      <table className="ticket-table">
        <thead>
          <tr>
            <th className="ticket-table__th ticket-table__th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={input => {
                  if (input) input.indeterminate = someSelected;
                }}
                onChange={(e) => onSelectAll(e.target.checked)}
                aria-label="Select all tickets"
              />
            </th>
            <th className="ticket-table__th ticket-table__th--ticket">
              <button className="ticket-table__sort-btn" onClick={() => onSort('ticketNumber')}>
                Ticket
                <SortIcon field="ticketNumber" currentField={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className="ticket-table__th ticket-table__th--type">Type</th>
            <th className="ticket-table__th ticket-table__th--description">Description</th>
            <th className="ticket-table__th ticket-table__th--priority">
              <button className="ticket-table__sort-btn" onClick={() => onSort('priority')}>
                Pri
                <SortIcon field="priority" currentField={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className="ticket-table__th ticket-table__th--status">
              <button className="ticket-table__sort-btn" onClick={() => onSort('status')}>
                Status
                <SortIcon field="status" currentField={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className="ticket-table__th ticket-table__th--tech">
              <button className="ticket-table__sort-btn" onClick={() => onSort('tech')}>
                Assigned To
                <SortIcon field="tech" currentField={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className="ticket-table__th ticket-table__th--location">Location</th>
            <th className="ticket-table__th ticket-table__th--sla">
              <button className="ticket-table__sort-btn" onClick={() => onSort('sla')}>
                SLA
                <SortIcon field="sla" currentField={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className="ticket-table__th ticket-table__th--created">
              <button className="ticket-table__sort-btn" onClick={() => onSort('created')}>
                Created
                <SortIcon field="created" currentField={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className="ticket-table__th ticket-table__th--actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map(ticket => {
            const tech = getTech(ticket.assignedTechId);
            const slaStatus = getSlaStatus(ticket);
            const isSelected = selectedTicketIds.has(ticket.id);
            const isExpanded = expandedTicketId === ticket.id;

            return (
              <React.Fragment key={ticket.id}>
                <tr
                  className={`ticket-table__row ${isSelected ? 'ticket-table__row--selected' : ''} ${isExpanded ? 'ticket-table__row--expanded' : ''}`}
                >
                  <td className="ticket-table__td ticket-table__td--checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => onSelectTicket(ticket.id, e.target.checked)}
                      aria-label={`Select ticket ${ticket.ticketNumber}`}
                    />
                  </td>
                  <td className="ticket-table__td ticket-table__td--ticket">
                    <button
                      className="ticket-table__ticket-link"
                      onClick={() => onViewDetails(ticket)}
                    >
                      {ticket.ticketNumber}
                    </button>
                  </td>
                  <td className="ticket-table__td ticket-table__td--type">
                    <span
                      className="ticket-table__type-badge"
                      style={{
                        backgroundColor: TYPE_BADGE_COLORS[ticket.ticketType].bg,
                        color: TYPE_BADGE_COLORS[ticket.ticketType].text,
                        borderColor: TYPE_BADGE_COLORS[ticket.ticketType].border,
                      }}
                    >
                      {ticket.ticketType}
                    </span>
                  </td>
                  <td className="ticket-table__td ticket-table__td--description">
                    <span className="ticket-table__description" title={ticket.shortDescription}>
                      {ticket.shortDescription.length > 45
                        ? `${ticket.shortDescription.substring(0, 45)}...`
                        : ticket.shortDescription}
                    </span>
                  </td>
                  <td className="ticket-table__td ticket-table__td--priority">
                    <span className={`ticket-table__priority ticket-table__priority--${ticket.priority.toLowerCase()}`}>
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="ticket-table__td ticket-table__td--status">
                    <span className={`ticket-table__status ticket-table__status--${ticket.status.toLowerCase().replace(/\s+/g, '-')}`}>
                      {ticket.status}
                    </span>
                  </td>
                  <td className="ticket-table__td ticket-table__td--tech">
                    {tech ? (
                      <div className="ticket-table__tech">
                        <span className="ticket-table__tech-avatar">{tech.name.charAt(0)}</span>
                        <span className="ticket-table__tech-name">{tech.name.split(' ')[0]}</span>
                      </div>
                    ) : (
                      <span className="ticket-table__unassigned">Unassigned</span>
                    )}
                  </td>
                  <td className="ticket-table__td ticket-table__td--location">
                    <span className="ticket-table__location">{ticket.location.split(' - ')[1] || ticket.location}</span>
                  </td>
                  <td className="ticket-table__td ticket-table__td--sla">
                    <span className={`ticket-table__sla ticket-table__sla--${slaStatus}`}>
                      {slaStatus === 'breached' && (
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 8a.75.75 0 01-1.5 0V5a.75.75 0 011.5 0v3z" />
                        </svg>
                      )}
                      {slaStatus === 'at-risk' && (
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zM7.25 4.75a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zm.75 7.25a1 1 0 100-2 1 1 0 000 2z" />
                        </svg>
                      )}
                      {formatSlaTime(ticket.slaDeadline, slaStatus)}
                    </span>
                  </td>
                  <td className="ticket-table__td ticket-table__td--created">
                    <span className="ticket-table__created">{formatTimeAgo(ticket.createdAt)}</span>
                  </td>
                  <td className="ticket-table__td ticket-table__td--actions">
                    <div className="ticket-table__actions">
                      <button
                        className="ticket-table__action-btn"
                        onClick={() => onExpandTicket(ticket.id)}
                        title={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        >
                          <path d="M2 4L6 8L10 4" fill="none" stroke="#605e5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <button
                        className="ticket-table__action-btn"
                        onClick={() => onReassign(ticket.id)}
                        title="Reassign"
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16">
                          <circle cx="6" cy="5" r="2.5" fill="#605e5c" />
                          <path d="M2 13c0-2 1.5-3.5 4-3.5s4 1.5 4 3.5" fill="#605e5c" />
                          <path d="M11 7l2.5 2.5L11 12" fill="none" stroke="#605e5c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M13.5 9.5H9" fill="none" stroke="#605e5c" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                      <button
                        className="ticket-table__action-btn"
                        onClick={() => onHold(ticket.id)}
                        title="Put on hold"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12">
                          <rect x="1" y="1" width="4" height="10" rx="1" fill="#605e5c" />
                          <rect x="7" y="1" width="4" height="10" rx="1" fill="#605e5c" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expanded row */}
                {isExpanded && (
                  <tr className="ticket-table__expanded-row">
                    <td colSpan={11}>
                      <div className="ticket-table__expanded-content">
                        <div className="ticket-table__expanded-section">
                          <h4>Full Description</h4>
                          <p>{ticket.shortDescription}</p>
                        </div>

                        <div className="ticket-table__expanded-section">
                          <h4>Tags</h4>
                          <div className="ticket-table__expanded-tags">
                            {(ticket.tags ?? []).map(tag => (
                              <span key={tag} className="ticket-table__expanded-tag">{tag}</span>
                            ))}
                          </div>
                        </div>

                        {ticket.notes && (
                          <div className="ticket-table__expanded-section">
                            <h4>Notes</h4>
                            <p>{ticket.notes}</p>
                          </div>
                        )}

                        <div className="ticket-table__expanded-section">
                          <h4>Details</h4>
                          <div className="ticket-table__expanded-details">
                            <div className="ticket-table__expanded-detail">
                              <span className="ticket-table__expanded-label">Created</span>
                              <span className="ticket-table__expanded-value">
                                {new Date(ticket.createdAt).toLocaleString()}
                              </span>
                            </div>
                            <div className="ticket-table__expanded-detail">
                              <span className="ticket-table__expanded-label">Updated</span>
                              <span className="ticket-table__expanded-value">
                                {new Date(ticket.updatedAt).toLocaleString()}
                              </span>
                            </div>
                            <div className="ticket-table__expanded-detail">
                              <span className="ticket-table__expanded-label">SLA Deadline</span>
                              <span className="ticket-table__expanded-value">
                                {ticket.slaDeadline
                                  ? new Date(ticket.slaDeadline).toLocaleString()
                                  : 'N/A'}
                              </span>
                            </div>
                            <div className="ticket-table__expanded-detail">
                              <span className="ticket-table__expanded-label">Assigned Tech</span>
                              <span className="ticket-table__expanded-value">
                                {tech ? `${tech.name} (${tech.location})` : 'Unassigned'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="ticket-table__expanded-actions">
                          <button
                            className="ticket-table__expanded-btn ticket-table__expanded-btn--primary"
                            onClick={() => onViewDetails(ticket)}
                          >
                            View Full Details
                          </button>
                          <button
                            className="ticket-table__expanded-btn"
                            onClick={() => onReassign(ticket.id)}
                          >
                            Reassign
                          </button>
                          <button
                            className="ticket-table__expanded-btn"
                            onClick={() => onChangePriority(ticket.id)}
                          >
                            Change Priority
                          </button>
                          <a
                            className="ticket-table__expanded-btn"
                            href={`https://percepta.service-now.com/nav_to.do?uri=incident.do?sysparm_query=number=${ticket.ticketNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open in ServiceNow
                          </a>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
